import React from 'react'
import { IconButton } from '@material-ui/core'
import { t } from 'ttag'
import { ReactComponent as RemoveIcon } from '../../assets/v3/icons/remove.svg'
import { darkBlue } from '../../styles/material-ui-theme-v2'
import { isRtl } from '../../i18nInit'
import { YumDangerTooltip } from '../YumTooltip/YumTooltip'

interface YumRemoveButtonProps {
  /**
   * Click handler function for the button
   */
  onClick: () => void

  /**
   * Button label text (optional - if not provided, button shows icon only)
   */
  label?: string

  /**
   * ARIA label for accessibility (defaults to "delete")
   */
  ariaLabel?: string

  /**
   * Additional styles to apply to the button
   */
  style?: React.CSSProperties

  /**
   * Whether to show tooltip when label is not provided (defaults to true for icon-only buttons)
   */
  showTooltip?: boolean
}

/**
 * Reusable button component with remove icon and optional label text.
 * Supports RTL layout and customizable styling.
 * If no label is provided, displays as icon-only button with tooltip.
 */
export default function YumRemoveButton({
  onClick,
  label,
  ariaLabel = 'delete',
  style = {},
  showTooltip = true,
}: YumRemoveButtonProps) {
  // When label is provided, show it as text. When not provided, show tooltip on icon-only button.
  const buttonContent = (
    <IconButton aria-label={ariaLabel} onClick={onClick} style={{ borderRadius: '10px', ...style }}>
      <RemoveIcon
        style={{
          width: 24,
          height: 24,
        }}
      />
      {label && (
        <span
          style={{
            marginLeft: isRtl ? 0 : '10px',
            marginRight: !isRtl ? 0 : '10px',
            fontSize: '15px',
            color: darkBlue,
          }}
        >
          {label}
        </span>
      )}
    </IconButton>
  )

  // Show tooltip for icon-only buttons (when label is not provided)
  if (!label && showTooltip) {
    return (
      <YumDangerTooltip title={t`Remove`} arrow placement="top">
        {buttonContent}
      </YumDangerTooltip>
    )
  }

  return buttonContent
}
